import React, { useState, useEffect } from 'react';
import { Building, Search, PlayCircle, Clock, ArrowLeft } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { posthog } from 'posthog-js';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import {
  getVideoMetadataEntities,
  getVideoMetadataVideos,
  type VideoMetadataVideo,
} from '../../services/videoMetadataService';
import { getFilesBatch } from '../../services/fileSystemService';
import { getFileUrl, getShareNavigationPath } from '../../lib/routingUtils';
import type { RootState } from '../../reducers';

interface CompaniesModalProps {
  open: boolean;
  onClose: () => void;
}

interface CompanyItem {
  name: string;
  videoCount: number;
  loading?: boolean;
}

interface VideoItem extends VideoMetadataVideo {
  thumbnail?: string;
  duration?: string;
  loading?: boolean;
  error?: boolean;
}

const formatRelativeTime = (dateString: string): string => {
  if (!dateString || dateString.trim() === '') {
    return 'Recently';
  }

  const now = new Date();
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return 'Recently';
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const CompaniesModal: React.FC<CompaniesModalProps> = ({ open, onClose }) => {
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const navigate = useNavigate();
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId;

  const fetchFileDetailsBatch = async (recordingIds: string[]) => {
    const fileIds: string[] = [];
    const shareIds: string[] = [];

    recordingIds.forEach((id) => {
      if (id.length < 20) {
        shareIds.push(id);
      } else {
        fileIds.push(id);
      }
    });

    try {
      const response = await getFilesBatch({ fileIds, shareIds });
      return response?.files || {};
    } catch (fetchError) {
      console.warn('Failed to batch fetch file details:', fetchError);
      return {};
    }
  };

  useEffect(() => {
    if (!open || !teamId) return;

    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);

        const entitiesResponse = await getVideoMetadataEntities(teamId);

        if (entitiesResponse.success && entitiesResponse.data) {
          const { companies: companiesList, companiesWithCounts } =
            entitiesResponse.data;

          let companiesWithCountsData: CompanyItem[];

          // Use counts from API if available, otherwise fallback to individual API calls
          if (companiesWithCounts && companiesWithCounts.length > 0) {
            // Optimized: Use pre-calculated counts from single API call
            companiesWithCountsData = companiesWithCounts.map((company) => ({
              name: company.name,
              videoCount: company.count,
            }));
          } else {
            // Fallback: Make individual API calls (legacy behavior)
            console.warn(
              'Using fallback individual API calls for company counts',
            );
            companiesWithCountsData = await Promise.all(
              companiesList.map(async (company) => {
                try {
                  const videosResponse = await getVideoMetadataVideos(teamId, {
                    companyName: company,
                    limit: 1, // Just to get the count
                  });

                  return {
                    name: company,
                    videoCount: videosResponse.success
                      ? videosResponse.data.pagination.total
                      : 0,
                  };
                } catch {
                  return {
                    name: company,
                    videoCount: 0,
                  };
                }
              }),
            );
          }

          setCompanies(
            companiesWithCountsData.sort((a, b) => b.videoCount - a.videoCount),
          );
        } else {
          setError('Failed to load companies data');
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [open, teamId]);

  const handleCompanyClick = async (companyName: string) => {
    if (!teamId) return;

    posthog.capture('companies-modal-company-clicked', {
      companyName,
      teamId,
    });

    setSelectedCompany(companyName);
    setVideos([]);
    setSearchQuery(''); // Clear search when navigating to company's videos
    setVideosLoading(true);

    try {
      const videosResponse = await getVideoMetadataVideos(teamId, {
        companyName,
        page: 1,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (videosResponse.success && videosResponse.data) {
        const { videos: videoList } = videosResponse.data;

        // Create initial video items with loading state
        const videoItems: VideoItem[] = videoList.map((video) => ({
          ...video,
          loading: true,
        }));
        setVideos(videoItems);

        // Batch fetch all file details in a single API call
        const recordingIds = videoList.map((v) => v.recordingId);
        const batchResult = await fetchFileDetailsBatch(recordingIds);

        // Map batch results back to video items
        setVideos((prevVideos) =>
          prevVideos.map((video) => {
            const fileInfo = batchResult[video.recordingId];
            if (fileInfo) {
              return {
                ...video,
                thumbnail: fileInfo.thumbUrl,
                duration: fileInfo.duration
                  ? formatDuration(fileInfo.duration)
                  : undefined,
                loading: false,
              };
            }
            return { ...video, loading: false, error: true };
          }),
        );
      }
    } catch (err) {
      console.error('Error fetching company videos:', err);
      setError('Failed to load videos');
    } finally {
      setVideosLoading(false);
    }
  };

  const handleVideoClick = (video: VideoItem) => {
    if (!teamId) return;

    posthog.capture('companies-modal-video-clicked', {
      recordingId: video.recordingId,
      fileName: video.fileName,
      companyName: selectedCompany,
    });

    onClose();

    const isSharedFile = video.recordingId.length < 20;
    if (isSharedFile) {
      window.location.href = getShareNavigationPath(video.recordingId);
      return;
    }

    const fileUrl = getFileUrl({
      teamId,
      parentId: 'default',
      fileId: video.recordingId,
    });
    navigate(fileUrl);
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
    setVideos([]);
    setSearchQuery('');
  };

  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredVideos = videos.filter((video) =>
    video.fileName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col sm:max-w-[600px]">
        {/* Search Header */}
        {selectedCompany ? (
          <div>
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackToCompanies}
                className="h-8 w-8 -ml-2 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h2 className="font-semibold text-base">{selectedCompany}</h2>
            </div>
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base"
                disabled={loading || videosLoading}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-base mr-8"
              disabled={loading || videosLoading}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          <div className="overflow-y-auto overflow-x-hidden w-full">
            {(() => {
              if (loading || videosLoading) {
                return (
                  <div className="pt-3">
                    <div className="space-y-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="flex gap-3 px-6 py-2.5">
                          {selectedCompany ? (
                            <>
                              <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                              <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-full max-w-[70%]" />
                                <Skeleton className="h-3 w-full max-w-[40%]" />
                              </div>
                            </>
                          ) : (
                            <>
                              <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
                              <div className="flex-1 space-y-1 pt-1">
                                <Skeleton className="h-4 w-full max-w-[60%]" />
                                <Skeleton className="h-3 w-full max-w-[30%]" />
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {error}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please try refreshing the page
                      </p>
                    </div>
                  </div>
                );
              }

              if (selectedCompany) {
                // Show videos for selected company
                if (filteredVideos.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-sm text-gray-500">
                        {searchQuery
                          ? 'No videos found'
                          : 'No videos available'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="pt-3">
                    {filteredVideos.map((video, index) => (
                      <button
                        key={`${video.recordingId}-${index}`}
                        onClick={() => handleVideoClick(video)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleVideoClick(video);
                          }
                        }}
                        type="button"
                        className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
                      >
                        <div className="relative flex-shrink-0">
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt={video.fileName}
                              className="w-12 h-8 object-cover rounded bg-gray-100 dark:bg-gray-800"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const wrapper = target.parentElement!;
                                wrapper.className =
                                  'w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0';
                                wrapper.innerHTML =
                                  '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                              <PlayCircle className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm line-clamp-1">
                              {video.loading ? (
                                <span className="flex items-center gap-1.5 min-w-[100px]">
                                  <Clock className="w-3 h-3 flex-shrink-0" />
                                  <span>Loading...</span>
                                </span>
                              ) : (
                                video.fileName
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatRelativeTime(video.createdAt)}
                            </div>
                          </div>
                          {video.duration && video.duration !== 'Unknown' && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {video.duration}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              }

              // Show companies list
              if (filteredCompanies.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">
                      {searchQuery
                        ? 'No companies found'
                        : 'No companies data available'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="pt-3">
                  {filteredCompanies.map((company) => (
                    <button
                      key={company.name}
                      onClick={() => handleCompanyClick(company.name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCompanyClick(company.name);
                        }
                      }}
                      type="button"
                      className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
                    >
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <Building className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm">{company.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {company.videoCount}{' '}
                          {company.videoCount === 1 ? 'video' : 'videos'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompaniesModal;
